const STYLE_ID = 'ttoria-control-centre-styles-v1';

export const CONTROL_CENTRE_STYLES = `
:root {
  --cc-font: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

[data-ttoria-theme="dark"] {
  color-scheme: dark;
  --ui-scroll-track: #142945;
  --ui-scroll-thumb: #719bdd;
  --cc-page: #06101f;
  --cc-page-glow: rgba(27, 106, 255, .16);
  --cc-sidebar: rgba(7, 20, 40, .96);
  --cc-surface: #0b1930;
  --cc-surface-strong: #10213d;
  --cc-surface-soft: #132744;
  --cc-raised: #172d4b;
  --cc-line: rgba(137, 177, 236, .19);
  --cc-line-strong: rgba(85, 163, 255, .38);
  --cc-text: #f4f7ff;
  --cc-muted: #a7b6cb;
  --cc-subtle: #7f91aa;
  --cc-blue: #4c8dff;
  --cc-blue-strong: #2563eb;
  --cc-blue-soft: rgba(76, 141, 255, .14);
  --cc-cyan: #49d8ee;
  --cc-green: #45d7a3;
  --cc-green-soft: rgba(69, 215, 163, .13);
  --cc-amber: #f3bb52;
  --cc-amber-soft: rgba(243, 187, 82, .14);
  --cc-red: #fb7185;
  --cc-red-soft: rgba(251, 113, 133, .14);
  --cc-purple: #a78bfa;
  --cc-purple-soft: rgba(167, 139, 250, .14);
  --cc-shadow: 0 24px 70px rgba(0, 0, 0, .30);
  --cc-modal-shadow: 0 30px 100px rgba(0, 0, 0, .55);
}

[data-ttoria-theme="light"] {
  color-scheme: light;
  --ui-scroll-track: #e5ecf7;
  --ui-scroll-thumb: #426b9e;
  --cc-page: #f4f7fc;
  --cc-page-glow: rgba(37, 99, 235, .10);
  --cc-sidebar: rgba(255, 255, 255, .96);
  --cc-surface: #ffffff;
  --cc-surface-strong: #f8faff;
  --cc-surface-soft: #eef4ff;
  --cc-raised: #e6efff;
  --cc-line: rgba(31, 65, 114, .15);
  --cc-line-strong: rgba(37, 99, 235, .31);
  --cc-text: #10213d;
  --cc-muted: #53647b;
  --cc-subtle: #718198;
  --cc-blue: #2563eb;
  --cc-blue-strong: #1749bb;
  --cc-blue-soft: rgba(37, 99, 235, .09);
  --cc-cyan: #087f93;
  --cc-green: #07845d;
  --cc-green-soft: rgba(5, 150, 105, .10);
  --cc-amber: #9a5d00;
  --cc-amber-soft: rgba(217, 119, 6, .11);
  --cc-red: #c72c48;
  --cc-red-soft: rgba(225, 29, 72, .09);
  --cc-purple: #6d43cd;
  --cc-purple-soft: rgba(124, 58, 237, .09);
  --cc-shadow: 0 18px 55px rgba(26, 54, 93, .12);
  --cc-modal-shadow: 0 30px 90px rgba(26, 54, 93, .24);
}

.ttoria-control-centre,
.ttoria-control-centre * {
  box-sizing: border-box;
}

.ttoria-control-centre {
  min-height: 100%;
  min-height: 100dvh;
  width: 100%;
  color: var(--cc-text);
  background:
    radial-gradient(900px 520px at 4% -5%, var(--cc-page-glow), transparent 64%),
    radial-gradient(760px 500px at 100% 0%, rgba(73, 216, 238, .07), transparent 70%),
    var(--cc-page);
  font-family: var(--cc-font);
  font-size: 15px;
  line-height: 1.5;
}

/*
 * TToria owns the visible application chrome while this plugin is mounted.
 * The platform shell stays in the DOM so its loading, authentication, plugin
 * lifecycle and accessibility contracts remain intact; only its duplicate
 * presentation is collapsed for this one application.
 */
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) {
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .ds-shell-layout.platform-shell-layout {
  grid-template-columns: minmax(0, 1fr) !important;
  gap: 0 !important;
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100dvh !important;
  min-height: 100dvh !important;
  padding: 0 !important;
  transform: none !important;
}

body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .ds-shell-layout.platform-shell-layout > .ds-shell-layout__nav,
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .platform-shell-layout__status-shell {
  display: none !important;
}

body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .ds-shell-layout.platform-shell-layout > .ds-shell-layout__main,
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .ds-shell-layout.platform-shell-layout > .ds-shell-layout__main > .ds-shell-layout__content,
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) #platformRoot,
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .platform-shell-plugin-stage,
body:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .platform-shell-plugin-host {
  width: 100% !important;
  max-width: none !important;
  min-width: 0 !important;
  height: 100dvh !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: hidden !important;
}

.cc-skip-link {
  position: fixed;
  z-index: 1000;
  top: 12px;
  left: 12px;
  transform: translateY(-180%);
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--cc-blue-strong);
  color: white;
  font-weight: 750;
}

.cc-skip-link:focus {
  transform: translateY(0);
}

.cc-app {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

.cc-sidebar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  max-height: 100dvh;
  padding: 20px 16px 16px;
  overflow: auto;
  border-right: 1px solid var(--cc-line);
  background: var(--cc-sidebar);
  backdrop-filter: blur(24px);
}

.cc-brand {
  display: grid;
  flex: 0 0 auto;
  justify-items: center;
  gap: 3px;
  padding: 0 4px 12px;
  overflow: hidden;
}

.cc-brand__logo {
  display: block;
  width: min(160%, 332px);
  max-width: none;
  height: 112px;
  object-fit: cover;
  object-position: center;
  filter:
    brightness(1.15)
    saturate(1.12)
    contrast(1.06)
    drop-shadow(0 0 1px rgba(236, 246, 255, .7))
    drop-shadow(0 0 10px rgba(25, 141, 255, .26))
    drop-shadow(0 12px 22px rgba(1, 7, 22, .44));
}

[data-ttoria-theme="light"] .cc-brand__logo {
  filter:
    brightness(1.01)
    saturate(1.04)
    contrast(1.05)
    drop-shadow(0 7px 15px rgba(7, 35, 86, .17));
}

.cc-brand__product {
  color: var(--cc-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.cc-theme-control {
  display: grid;
  gap: 6px;
  margin: 0 4px 14px;
  padding: 9px;
  border: 1px solid var(--cc-line);
  border-radius: 12px;
  background: var(--cc-surface-strong);
}

.cc-theme-control__label {
  color: var(--cc-subtle);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.cc-theme-switch {
  appearance: none;
  display: grid;
  grid-template-columns: 1fr 38px 1fr;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 4px 7px;
  border: 0;
  border-radius: 9px;
  color: var(--cc-muted);
  background: transparent;
  font: inherit;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
}

.cc-theme-switch__label:first-child { text-align: right; }
.cc-theme-switch__label:last-child { text-align: left; }
.cc-theme-switch[aria-checked="false"] .cc-theme-switch__label:first-child,
.cc-theme-switch[aria-checked="true"] .cc-theme-switch__label:last-child { color: var(--cc-text); }

.cc-theme-switch__track {
  position: relative;
  width: 38px;
  height: 22px;
  border: 1px solid var(--cc-line-strong);
  border-radius: 999px;
  background: var(--cc-raised);
}

.cc-theme-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--cc-text);
  box-shadow: 0 2px 7px rgba(0, 0, 0, .28);
  transition: transform .18s ease;
}

.cc-theme-switch[aria-checked="true"] .cc-theme-switch__thumb {
  transform: translateX(16px);
}

.cc-nav-label {
  margin: 4px 10px 8px;
  color: var(--cc-subtle);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.cc-nav {
  display: grid;
  gap: 5px;
}

.cc-nav__button {
  appearance: none;
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: 44px;
  padding: 9px 11px;
  border: 1px solid transparent;
  border-radius: 11px;
  color: var(--cc-muted);
  background: transparent;
  font: inherit;
  font-weight: 680;
  text-align: left;
  cursor: pointer;
  transition: color .16s ease, background .16s ease, border-color .16s ease, transform .16s ease;
}

.cc-nav__button:hover {
  color: var(--cc-text);
  background: var(--cc-blue-soft);
}

.cc-nav__button[aria-current="page"] {
  color: white;
  border-color: rgba(101, 160, 255, .45);
  background: linear-gradient(135deg, #2167dc, #3e55e9);
  box-shadow: 0 12px 26px rgba(37, 99, 235, .25);
}

[data-ttoria-theme="light"] .cc-nav__button[aria-current="page"] {
  color: white;
}

.cc-nav__button:active {
  transform: translateY(1px);
}

.cc-nav__button svg {
  width: 19px;
  height: 19px;
}

.cc-nav__badge {
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 999px;
  background: var(--cc-red);
  color: white;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.cc-sidebar__bottom {
  display: grid;
  gap: 10px;
  margin-top: auto;
  padding-top: 18px;
}

.cc-trust-card {
  padding: 13px;
  border: 1px solid var(--cc-line);
  border-radius: 13px;
  background: linear-gradient(145deg, var(--cc-blue-soft), transparent);
}

.cc-trust-card strong {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
}

.cc-trust-card p {
  margin: 7px 0 0;
  color: var(--cc-muted);
  font-size: 11px;
}

.cc-sidebar__footer {
  display: grid;
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--cc-line);
  border-radius: 12px;
  background: var(--cc-surface-strong);
}

.cc-sidebar__logout {
  width: 100%;
  justify-content: center;
}

.cc-owner {
  min-width: 0;
}

.cc-owner strong,
.cc-owner span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-owner strong {
  font-size: 12px;
}

.cc-owner span {
  color: var(--cc-muted);
  font-size: 10px;
}

.cc-main {
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 18px clamp(16px, 2.4vw, 34px) 48px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
}

.cc-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 66px;
  margin: -18px calc(clamp(16px, 2.4vw, 34px) * -1) 22px;
  padding: 11px clamp(16px, 2.4vw, 34px);
  border-bottom: 1px solid var(--cc-line);
  background: color-mix(in srgb, var(--cc-page) 86%, transparent);
  backdrop-filter: blur(20px);
}

.cc-topbar__start,
.cc-topbar__end {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cc-topbar__title {
  min-width: 0;
}

.cc-topbar__title strong,
.cc-topbar__title span {
  display: block;
}

.cc-topbar__title strong {
  overflow: hidden;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-topbar__title span {
  color: var(--cc-muted);
  font-size: 11px;
}

.cc-menu-button {
  display: none;
}

.cc-icon-button {
  appearance: none;
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border: 1px solid var(--cc-line);
  border-radius: 11px;
  color: var(--cc-text);
  background: var(--cc-surface);
  cursor: pointer;
}

.cc-icon-button:hover {
  border-color: var(--cc-line-strong);
  background: var(--cc-surface-soft);
}

.cc-icon-button svg {
  width: 19px;
  height: 19px;
}

.cc-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 20px;
}

.cc-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: var(--cc-cyan);
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .11em;
  text-transform: uppercase;
}

.cc-page-head h1 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(28px, 3.5vw, 42px);
  line-height: 1.08;
  letter-spacing: -.025em;
}

.cc-page-head p {
  max-width: 730px;
  margin: 8px 0 0;
  color: var(--cc-muted);
}

.cc-page-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 9px;
}

.cc-button {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 14px;
  border: 1px solid var(--cc-line);
  border-radius: 10px;
  color: var(--cc-text);
  background: var(--cc-surface-strong);
  font: inherit;
  font-size: 13px;
  font-weight: 760;
  text-decoration: none;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease, transform .16s ease, opacity .16s ease;
}

.cc-button:hover:not(:disabled) {
  border-color: var(--cc-line-strong);
  background: var(--cc-raised);
}

.cc-button:active:not(:disabled) {
  transform: translateY(1px);
}

.cc-button:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.cc-button--primary {
  border-color: #3976ef;
  color: white;
  background: linear-gradient(135deg, #2674e8, #3347df);
  box-shadow: 0 10px 24px rgba(37, 99, 235, .22);
}

.cc-button--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #3181f8, #4256ef);
}

.cc-button--danger {
  border-color: color-mix(in srgb, var(--cc-red) 48%, transparent);
  color: var(--cc-red);
  background: var(--cc-red-soft);
}

.cc-button--quiet {
  border-color: transparent;
  background: transparent;
}

.cc-button svg {
  width: 17px;
  height: 17px;
}

.cc-button[data-kg-ui-state="busy"],
.cc-icon-button[data-kg-ui-state="busy"] {
  cursor: progress;
}

.cc-button[data-kg-ui-state="busy"]::before,
.cc-icon-button[data-kg-ui-state="busy"]::before {
  content: "";
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: cc-spin .7s linear infinite;
}

.cc-button[data-kg-ui-state="busy"] > svg,
.cc-icon-button[data-kg-ui-state="busy"] > svg {
  display: none;
}

.cc-grid {
  display: grid;
  gap: 14px;
}

.cc-grid--metrics {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.cc-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cc-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.cc-card {
  min-width: 0;
  padding: 18px;
  border: 1px solid var(--cc-line);
  border-radius: 15px;
  background:
    linear-gradient(145deg, color-mix(in srgb, var(--cc-surface-strong) 92%, var(--cc-blue-soft)), var(--cc-surface));
  box-shadow: 0 1px 0 rgba(255, 255, 255, .025);
}

.cc-card--accent {
  border-color: color-mix(in srgb, var(--cc-blue) 42%, var(--cc-line));
  background:
    radial-gradient(500px 190px at 0 0, var(--cc-blue-soft), transparent 74%),
    var(--cc-surface);
}

.cc-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.cc-card__head h2,
.cc-card__head h3 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  line-height: 1.22;
}

.cc-card__head h2 {
  font-size: 20px;
}

.cc-card__head h3 {
  font-size: 17px;
}

.cc-card__head p {
  margin: 4px 0 0;
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-metric {
  position: relative;
  min-height: 142px;
  overflow: hidden;
}

.cc-metric::after {
  content: "";
  position: absolute;
  right: -36px;
  bottom: -52px;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: var(--metric-soft, var(--cc-blue-soft));
  pointer-events: none;
}

.cc-metric__icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid color-mix(in srgb, var(--metric, var(--cc-blue)) 40%, transparent);
  border-radius: 12px;
  color: var(--metric, var(--cc-blue));
  background: var(--metric-soft, var(--cc-blue-soft));
}

.cc-metric__icon svg {
  width: 20px;
  height: 20px;
}

.cc-metric__label {
  display: block;
  margin-top: 14px;
  color: var(--cc-muted);
  font-size: 11px;
  font-weight: 820;
  letter-spacing: .07em;
  text-transform: uppercase;
}

.cc-metric__value {
  display: block;
  margin-top: 2px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 30px;
  line-height: 1.1;
}

.cc-metric__detail {
  display: block;
  margin-top: 4px;
  color: var(--cc-muted);
  font-size: 11px;
}

.cc-section {
  margin-top: 16px;
}

.cc-section + .cc-section {
  margin-top: 18px;
}

.cc-list {
  display: grid;
  gap: 9px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cc-list-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 13px;
  border: 1px solid var(--cc-line);
  border-radius: 12px;
  background: var(--cc-surface-strong);
}

.cc-list-item__icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  color: var(--cc-blue);
  background: var(--cc-blue-soft);
}

.cc-list-item__icon svg {
  width: 18px;
  height: 18px;
}

.cc-list-item__body {
  min-width: 0;
}

.cc-list-item__body strong,
.cc-list-item__body span {
  display: block;
}

.cc-list-item__body strong {
  font-size: 13px;
}

.cc-list-item__body span {
  margin-top: 2px;
  overflow-wrap: anywhere;
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  min-height: 25px;
  padding: 3px 8px;
  border: 1px solid var(--cc-line);
  border-radius: 999px;
  color: var(--cc-muted);
  background: var(--cc-surface-strong);
  font-size: 10px;
  font-weight: 820;
  letter-spacing: .025em;
  line-height: 1;
  text-transform: capitalize;
}

.cc-badge::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.cc-badge--ok {
  border-color: color-mix(in srgb, var(--cc-green) 32%, transparent);
  color: var(--cc-green);
  background: var(--cc-green-soft);
}

.cc-badge--warn {
  border-color: color-mix(in srgb, var(--cc-amber) 34%, transparent);
  color: var(--cc-amber);
  background: var(--cc-amber-soft);
}

.cc-badge--danger {
  border-color: color-mix(in srgb, var(--cc-red) 34%, transparent);
  color: var(--cc-red);
  background: var(--cc-red-soft);
}

.cc-badge--info {
  border-color: color-mix(in srgb, var(--cc-blue) 34%, transparent);
  color: var(--cc-blue);
  background: var(--cc-blue-soft);
}

.cc-alert {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 11px;
  padding: 13px 14px;
  border: 1px solid var(--cc-line);
  border-radius: 12px;
  background: var(--cc-surface-strong);
}

.cc-alert + .cc-alert {
  margin-top: 9px;
}

.cc-alert--warn {
  border-color: color-mix(in srgb, var(--cc-amber) 32%, transparent);
  background: var(--cc-amber-soft);
}

.cc-alert--danger {
  border-color: color-mix(in srgb, var(--cc-red) 32%, transparent);
  background: var(--cc-red-soft);
}

.cc-alert--info {
  border-color: color-mix(in srgb, var(--cc-blue) 32%, transparent);
  background: var(--cc-blue-soft);
}

.cc-alert__icon {
  color: var(--cc-blue);
}

.cc-alert--warn .cc-alert__icon { color: var(--cc-amber); }
.cc-alert--danger .cc-alert__icon { color: var(--cc-red); }

.cc-alert__icon svg {
  width: 19px;
  height: 19px;
}

.cc-alert strong,
.cc-alert span {
  display: block;
}

.cc-alert span {
  margin-top: 2px;
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) repeat(2, minmax(150px, 210px)) auto;
  gap: 10px;
  margin-bottom: 14px;
}

.cc-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.cc-field > span,
.cc-field legend {
  color: var(--cc-muted);
  font-size: 11px;
  font-weight: 780;
}

.cc-field input,
.cc-field select,
.cc-field textarea {
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid var(--cc-line);
  border-radius: 10px;
  outline: none;
  color: var(--cc-text);
  background: var(--cc-surface);
  font: inherit;
  font-size: 13px;
}

.cc-field textarea {
  min-height: 94px;
  resize: vertical;
}

.cc-field input:focus,
.cc-field select:focus,
.cc-field textarea:focus {
  border-color: var(--cc-blue);
  box-shadow: 0 0 0 3px var(--cc-blue-soft);
}

.cc-field [aria-invalid="true"] {
  border-color: var(--cc-red);
}

.cc-field__hint,
.cc-field__error {
  margin: 0;
  font-size: 11px;
}

.cc-field__hint { color: var(--cc-muted); }
.cc-field__error { color: var(--cc-red); }

.cc-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.cc-choice {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  min-height: 70px;
  padding: 12px;
  border: 1px solid var(--cc-line);
  border-radius: 12px;
  background: var(--cc-surface-strong);
  cursor: pointer;
}

.cc-choice:has(input:checked) {
  border-color: var(--cc-blue);
  background: var(--cc-blue-soft);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--cc-blue) 18%, transparent);
}

.cc-choice input {
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: var(--cc-blue);
}

.cc-choice strong,
.cc-choice small {
  display: block;
}

.cc-choice small {
  margin-top: 3px;
  color: var(--cc-muted);
}

.cc-table-wrap {
  max-width: 100%;
  overflow: auto;
  border: 1px solid var(--cc-line);
  border-radius: 13px;
  background: var(--cc-surface);
}

.cc-table {
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
}

.cc-table th,
.cc-table td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--cc-line);
  text-align: left;
  vertical-align: middle;
}

.cc-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  color: var(--cc-muted);
  background: var(--cc-surface-strong);
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .07em;
  text-transform: uppercase;
}

.cc-table td {
  font-size: 12px;
}

.cc-table tbody tr:hover {
  background: var(--cc-blue-soft);
}

.cc-table tbody tr:last-child td {
  border-bottom: 0;
}

.cc-table__primary {
  display: block;
  color: var(--cc-text);
  font-size: 13px;
  font-weight: 760;
}

.cc-table__secondary {
  display: block;
  margin-top: 2px;
  color: var(--cc-muted);
  font-size: 11px;
}

.cc-empty {
  display: grid;
  justify-items: center;
  gap: 8px;
  min-height: 250px;
  padding: 46px 20px;
  text-align: center;
}

.cc-empty__icon {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border: 1px solid var(--cc-line-strong);
  border-radius: 18px;
  color: var(--cc-blue);
  background: var(--cc-blue-soft);
}

.cc-empty__icon svg {
  width: 28px;
  height: 28px;
}

.cc-empty h2,
.cc-empty h3,
.cc-empty p {
  margin: 0;
}

.cc-empty h2,
.cc-empty h3 {
  font-family: Georgia, "Times New Roman", serif;
}

.cc-empty p {
  max-width: 500px;
  color: var(--cc-muted);
  font-size: 13px;
}

.cc-skeleton {
  position: relative;
  overflow: hidden;
  min-height: 122px;
}

.cc-skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(128, 170, 229, .10), transparent);
  animation: cc-shimmer 1.3s infinite;
}

.cc-skeleton__line {
  width: 68%;
  height: 12px;
  margin-top: 12px;
  border-radius: 999px;
  background: var(--cc-raised);
}

.cc-skeleton__line--short {
  width: 36%;
}

.cc-divider {
  height: 1px;
  margin: 15px 0;
  background: var(--cc-line);
}

.cc-kv {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}

.cc-kv > div {
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--cc-line);
  border-radius: 11px;
  background: var(--cc-surface-strong);
}

.cc-kv dt {
  color: var(--cc-muted);
  font-size: 10px;
  font-weight: 810;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.cc-kv dd {
  margin: 4px 0 0;
  overflow-wrap: anywhere;
  font-size: 13px;
  font-weight: 690;
}

.cc-tabs {
  display: flex;
  gap: 4px;
  max-width: 100%;
  margin-bottom: 14px;
  overflow-x: auto;
  scrollbar-width: thin;
}

.cc-tab {
  appearance: none;
  flex: 0 0 auto;
  min-height: 38px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 9px;
  color: var(--cc-muted);
  background: transparent;
  font: inherit;
  font-size: 12px;
  font-weight: 760;
  cursor: pointer;
}

.cc-tab:hover {
  color: var(--cc-text);
  background: var(--cc-surface-strong);
}

.cc-tab[aria-selected="true"] {
  border-color: var(--cc-line-strong);
  color: var(--cc-blue);
  background: var(--cc-blue-soft);
}

.cc-progress {
  display: grid;
  gap: 6px;
}

.cc-progress__meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: var(--cc-muted);
  font-size: 11px;
}

.cc-progress__track {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--cc-raised);
}

.cc-progress__bar {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--cc-blue), var(--cc-cyan), var(--cc-green));
}

.cc-timeline {
  position: relative;
  display: grid;
  gap: 11px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cc-timeline::before {
  content: "";
  position: absolute;
  top: 9px;
  bottom: 9px;
  left: 11px;
  width: 1px;
  background: var(--cc-line);
}

.cc-timeline__item {
  position: relative;
  display: grid;
  grid-template-columns: 23px minmax(0, 1fr);
  gap: 12px;
}

.cc-timeline__dot {
  z-index: 1;
  width: 9px;
  height: 9px;
  margin: 7px;
  border: 2px solid var(--cc-surface);
  border-radius: 50%;
  background: var(--cc-blue);
  box-shadow: 0 0 0 2px var(--cc-line-strong);
}

.cc-timeline__content {
  padding: 12px 13px;
  border: 1px solid var(--cc-line);
  border-radius: 11px;
  background: var(--cc-surface-strong);
}

.cc-timeline__content strong,
.cc-timeline__content span,
.cc-timeline__content time {
  display: block;
}

.cc-timeline__content span {
  margin-top: 3px;
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-timeline__content time {
  margin-top: 6px;
  color: var(--cc-subtle);
  font-size: 10px;
}

.cc-mobile-overlay {
  display: none;
}

.cc-modal-backdrop {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 18px;
  overflow: auto;
  background: rgba(2, 8, 20, .72);
  backdrop-filter: blur(7px);
}

.cc-modal {
  width: min(680px, 100%);
  max-height: calc(100dvh - 36px);
  overflow: auto;
  border: 1px solid var(--cc-line-strong);
  border-radius: 18px;
  color: var(--cc-text);
  background: var(--cc-surface);
  box-shadow: var(--cc-modal-shadow);
}

.cc-modal--wide {
  width: min(850px, 100%);
}

.cc-modal__head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--cc-line);
  background: color-mix(in srgb, var(--cc-surface) 92%, transparent);
  backdrop-filter: blur(14px);
}

.cc-modal__head h2,
.cc-modal__head p {
  margin: 0;
}

.cc-modal__head h2 {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 23px;
}

.cc-modal__head p {
  margin-top: 4px;
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-modal__body {
  padding: 20px;
}

.cc-modal__foot {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid var(--cc-line);
  background: color-mix(in srgb, var(--cc-surface) 94%, transparent);
  backdrop-filter: blur(14px);
}

.cc-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

.cc-steps {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 5px;
  margin: 0 0 18px;
}

.cc-step {
  height: 5px;
  border-radius: 999px;
  background: var(--cc-raised);
}

.cc-step[data-complete="true"],
.cc-step[data-current="true"] {
  background: var(--cc-blue);
}

.cc-step[data-current="true"] {
  box-shadow: 0 0 0 2px var(--cc-blue-soft);
}

.cc-review {
  display: grid;
  gap: 10px;
}

.cc-review__row {
  display: grid;
  grid-template-columns: minmax(130px, .45fr) minmax(0, 1fr);
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--cc-line);
}

.cc-review__row:last-child {
  border-bottom: 0;
}

.cc-review__row dt {
  color: var(--cc-muted);
  font-size: 12px;
}

.cc-review__row dd {
  margin: 0;
  overflow-wrap: anywhere;
  font-weight: 680;
}

.cc-receipt {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 14px 0;
  text-align: center;
}

.cc-receipt__icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  color: var(--cc-green);
  background: var(--cc-green-soft);
}

.cc-receipt__icon svg {
  width: 32px;
  height: 32px;
}

.cc-receipt h3,
.cc-receipt p {
  margin: 0;
}

.cc-receipt h3 {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 23px;
}

.cc-receipt p {
  max-width: 500px;
  color: var(--cc-muted);
}

.cc-code {
  display: inline-flex;
  max-width: 100%;
  padding: 5px 8px;
  overflow-wrap: anywhere;
  border: 1px solid var(--cc-line);
  border-radius: 8px;
  color: var(--cc-cyan);
  background: var(--cc-surface-strong);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
}

.cc-visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.cc-screen-status {
  margin-bottom: 14px;
}

.cc-freshness {
  color: var(--cc-muted);
  font-size: 11px;
  white-space: nowrap;
}

.cc-freshness[data-stale="true"] {
  color: var(--cc-amber);
}

.cc-body-locked {
  overflow: hidden !important;
}

body.cc-body-locked:has([data-testid="control-centre-root"][data-manage-shell-chrome="true"]) .cc-main {
  overflow-y: hidden;
}

.cc-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: cc-spin .7s linear infinite;
}

:where(.ttoria-control-centre button, .ttoria-control-centre a, .ttoria-control-centre input, .ttoria-control-centre select, .ttoria-control-centre textarea):focus-visible {
  outline: 3px solid color-mix(in srgb, var(--cc-cyan) 75%, white);
  outline-offset: 2px;
}

@keyframes cc-spin { to { transform: rotate(360deg); } }
@keyframes cc-shimmer { to { transform: translateX(100%); } }

@media (max-width: 1180px) {
  .cc-grid--metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .cc-grid--three { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .cc-app { grid-template-columns: 1fr; }
  .cc-sidebar {
    position: fixed;
    left: 0;
    transform: translateX(-105%);
    visibility: hidden;
    width: min(290px, calc(100vw - 48px));
    transition: transform .2s ease, visibility 0s linear .2s;
    box-shadow: var(--cc-shadow);
  }
  .ttoria-control-centre[data-menu-open="true"] .cc-sidebar {
    transform: translateX(0);
    visibility: visible;
    transition-delay: 0s;
  }
  .cc-mobile-overlay {
    position: fixed;
    z-index: 25;
    inset: 0;
    display: none;
    background: rgba(2, 8, 20, .58);
  }
  .ttoria-control-centre[data-menu-open="true"] .cc-mobile-overlay {
    display: block;
  }
  .cc-menu-button { display: inline-grid; }
  .cc-main { padding-inline: 16px; }
  .cc-topbar { margin-inline: -16px; padding-inline: 16px; }
  .cc-toolbar { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 680px) {
  .cc-main { padding-bottom: 30px; }
  .cc-topbar__end .cc-freshness { display: none; }
  .cc-page-head { display: grid; }
  .cc-page-actions { justify-content: flex-start; }
  .cc-grid--metrics,
  .cc-grid--two,
  .cc-grid--three,
  .cc-kv,
  .cc-choice-grid,
  .cc-toolbar {
    grid-template-columns: 1fr;
  }
  .cc-metric { min-height: 125px; }
  .cc-list-item { grid-template-columns: auto minmax(0, 1fr); }
  .cc-list-item > :last-child { grid-column: 2; justify-self: start; }
  .cc-modal-backdrop { place-items: end center; padding: 0; }
  .cc-modal,
  .cc-modal--wide {
    width: 100%;
    max-height: 94dvh;
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 18px 18px 0 0;
  }
  .cc-modal__head,
  .cc-modal__body,
  .cc-modal__foot { padding-inline: 16px; }
  .cc-modal__foot { align-items: stretch; flex-direction: column; }
  .cc-modal__actions { display: grid; grid-template-columns: 1fr 1fr; }
  .cc-modal__actions .cc-button { width: 100%; }
  .cc-review__row { grid-template-columns: 1fr; gap: 4px; }
}

@media (max-width: 380px) {
  .cc-main { padding-inline: 12px; }
  .cc-topbar { margin-inline: -12px; padding-inline: 12px; }
  .cc-topbar__title { display: none; }
  .cc-topbar__end { flex: 1; justify-content: flex-end; }
  .cc-page-head h1 { font-size: 27px; }
  .cc-card { padding: 14px; }
  .cc-topbar__title span { display: none; }
  .cc-modal__actions { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .ttoria-control-centre *,
  .ttoria-control-centre *::before,
  .ttoria-control-centre *::after {
    scroll-behavior: auto !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }
}

@media (forced-colors: active) {
  .cc-brand__mark,
  .cc-button,
  .cc-nav__button,
  .cc-card,
  .cc-badge,
  .cc-alert,
  .cc-table-wrap,
  .cc-field input,
  .cc-field select,
  .cc-field textarea {
    forced-color-adjust: auto;
  }
  .cc-badge::before { border: 1px solid currentColor; }
}
`;

const ICONS = Object.freeze({
  today: '<path d="M3 12h4l2-6 4 12 2-6h6"/><path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"/>',
  accounts: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  billing: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/>',
  usage: '<path d="M3 3v18h18"/><path d="m7 16 4-5 4 3 5-8"/>',
  operations: '<path d="M12 3v3M12 18v3M3 12h3M18 12h3"/><circle cx="12" cy="12" r="4"/><path d="m5.64 5.64 2.12 2.12M16.24 16.24l2.12 2.12M18.36 5.64l-2.12 2.12M7.76 16.24l-2.12 2.12"/>',
  incidents: '<path d="M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/>',
  security: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/>',
  audit: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6M8 13h8M8 17h6"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.63 15 1.7 1.7 0 0 0 3.08 14H3v-4h.08A1.7 1.7 0 0 0 4.63 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.63 1.7 1.7 0 0 0 10 3.08V3h4v.08A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.37 9 1.7 1.7 0 0 0 20.92 10H21v4h-.08A1.7 1.7 0 0 0 19.4 15Z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  refresh: '<path d="M20 6v5h-5"/><path d="M20 11a8 8 0 1 0 1 5"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
  chevron: '<path d="m9 18 6-6-6-6"/>',
  back: '<path d="m15 18-6-6 6-6"/>',
  close: '<path d="M18 6 6 18M6 6l12 12"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41"/>',
  moon: '<path d="M21 12.8A8.5 8.5 0 1 1 11.2 3 6.6 6.6 0 0 0 21 12.8Z"/>',
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
  check: '<path d="m5 12 4 4L19 6"/>',
  info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  lock: '<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
  key: '<circle cx="7.5" cy="15.5" r="3.5"/><path d="m10 13 9-9M15 8l2 2M17 6l2 2"/>',
  activity: '<path d="M3 12h4l2-6 4 12 2-6h6"/>',
  database: '<ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6"/>',
  users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  calendar: '<rect x="3" y="4" width="18" height="17" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  alert: '<path d="M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/>',
  external: '<path d="M14 3h7v7M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/>',
  retry: '<path d="M21 12a9 9 0 1 1-2.64-6.36L21 8"/><path d="M21 3v5h-5"/>',
  pause: '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>',
  play: '<path d="m6 3 14 9-14 9V3Z"/>',
  receipt: '<path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z"/><path d="M9 7h6M9 11h6M9 15h4"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  logout: '<path d="M10 17l5-5-5-5M15 12H3"/><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>',
});

export function ensureControlCentreStyles(doc = document) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CONTROL_CENTRE_STYLES;
  doc.head.append(style);
}

export function escapeHtml(value = '') {
  return String(value ?? '').replace(/[<>&"']/g, (character) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));
}

export function icon(name = 'info', { label = '', className = '' } = {}) {
  const body = ICONS[name] || ICONS.info;
  const aria = label
    ? `role="img" aria-label="${escapeHtml(label)}"`
    : 'aria-hidden="true" focusable="false"';
  return `<svg class="${escapeHtml(className)}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" ${aria}>${body}</svg>`;
}

export function cleanText(value = '', fallback = '') {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || fallback;
}

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function safeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function clampPercent(value) {
  return Math.max(0, Math.min(100, Math.round(safeNumber(value, 0))));
}

export function formatCount(value) {
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(Math.max(0, safeNumber(value, 0)));
}

export function formatMoney(value, currency = 'CAD') {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: cleanText(currency, 'CAD').toUpperCase(),
    maximumFractionDigits: 2,
  }).format(safeNumber(value, 0));
}

export function formatDate(value, fallback = 'Not available') {
  if (!value) return fallback;
  const normalized = cleanText(value);
  const date = /^\d{4}-\d{2}-\d{2}$/u.test(normalized)
    ? new Date(`${normalized}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return cleanText(value, fallback);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(value, fallback = 'Not available') {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return cleanText(value, fallback);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function statusTone(value = '') {
  const status = cleanText(value).toLowerCase().replace(/[\s-]+/g, '_');
  if ([
    'active', 'full', 'healthy', 'resolved', 'completed', 'delivered', 'ok',
    'succeeded', 'current', 'verified', 'available', 'green', 'pass',
  ].includes(status)) return 'ok';
  if ([
    'failed', 'critical', 'suspended', 'security_quarantine', 'closed',
    'bounced', 'unpaid', 'error', 'red', 'blocked', 'expired',
  ].includes(status)) return 'danger';
  if ([
    'past_due', 'grace', 'read_only', 'warning', 'degraded', 'waiting',
    'pending', 'provisioning', 'onboarding', 'trialing', 'stale', 'amber',
    'closure_pending', 'cancel_at_period_end',
  ].includes(status)) return 'warn';
  return 'info';
}

export function statusBadge(label = '', value = '') {
  const text = cleanText(label || value, 'Unknown');
  return `<span class="cc-badge cc-badge--${statusTone(value || label)}" data-ds-component="ds/badge">${escapeHtml(text)}</span>`;
}

export function actionButton({
  label = 'Continue',
  action = '',
  tone = '',
  iconName = '',
  testId = '',
  disabled = false,
  type = 'button',
  attrs = '',
} = {}) {
  const classes = ['cc-button', tone ? `cc-button--${tone}` : ''].filter(Boolean).join(' ');
  return `<button
    type="${escapeHtml(type)}"
    class="${classes}"
    ${action ? `data-cc-action="${escapeHtml(action)}"` : ''}
    ${testId ? `data-testid="${escapeHtml(testId)}"` : ''}
    ${disabled ? 'disabled aria-disabled="true"' : ''}
    data-ds-component="ds/button"
    ${attrs}
  >${iconName ? icon(iconName) : ''}<span>${escapeHtml(label)}</span></button>`;
}

export function metricCard({
  label = '',
  value = '0',
  detail = '',
  iconName = 'activity',
  color = 'var(--cc-blue)',
  soft = 'var(--cc-blue-soft)',
  testId = '',
} = {}) {
  return `<article
    class="cc-card cc-metric"
    style="--metric:${escapeHtml(color)};--metric-soft:${escapeHtml(soft)}"
    data-ds-component="ds/card"
    ${testId ? `data-testid="${escapeHtml(testId)}"` : ''}
  >
    <span class="cc-metric__icon">${icon(iconName)}</span>
    <span class="cc-metric__label">${escapeHtml(label)}</span>
    <strong class="cc-metric__value">${escapeHtml(value)}</strong>
    ${detail ? `<span class="cc-metric__detail">${escapeHtml(detail)}</span>` : ''}
  </article>`;
}

export function loadingCards(count = 4) {
  return `<div class="cc-grid cc-grid--metrics" aria-busy="true" aria-label="Loading">
    ${Array.from({ length: count }, () => `
      <div class="cc-card cc-skeleton" data-ds-component="ds/card">
        <div class="cc-skeleton__line cc-skeleton__line--short"></div>
        <div class="cc-skeleton__line"></div>
        <div class="cc-skeleton__line cc-skeleton__line--short"></div>
      </div>
    `).join('')}
  </div>`;
}

export function emptyState({
  title = 'Nothing to show yet',
  detail = 'New information will appear here when it becomes available.',
  iconName = 'info',
  actionHtml = '',
  testId = '',
} = {}) {
  return `<section class="cc-card cc-empty" data-ds-component="ds/empty-state" ${testId ? `data-testid="${escapeHtml(testId)}"` : ''}>
    <span class="cc-empty__icon">${icon(iconName)}</span>
    <h2>${escapeHtml(title)}</h2>
    <p>${escapeHtml(detail)}</p>
    ${actionHtml}
  </section>`;
}

export function alertBox({
  title = '',
  detail = '',
  tone = 'info',
  iconName = 'info',
  role = '',
  testId = '',
} = {}) {
  return `<div
    class="cc-alert cc-alert--${escapeHtml(tone)}"
    data-ds-component="ds/alert"
    ${role ? `role="${escapeHtml(role)}"` : ''}
    ${testId ? `data-testid="${escapeHtml(testId)}"` : ''}
  >
    <span class="cc-alert__icon">${icon(iconName)}</span>
    <span><strong>${escapeHtml(title)}</strong>${detail ? `<span>${escapeHtml(detail)}</span>` : ''}</span>
  </div>`;
}

export function pageHeading({
  eyebrow = 'TToria Control Centre',
  title = '',
  detail = '',
  actionsHtml = '',
  testId = '',
} = {}) {
  return `<header class="cc-page-head" ${testId ? `data-testid="${escapeHtml(testId)}"` : ''}>
    <div>
      <span class="cc-eyebrow">${escapeHtml(eyebrow)}</span>
      <h1 tabindex="-1" data-cc-page-heading>${escapeHtml(title)}</h1>
      ${detail ? `<p>${escapeHtml(detail)}</p>` : ''}
    </div>
    ${actionsHtml ? `<div class="cc-page-actions">${actionsHtml}</div>` : ''}
  </header>`;
}

export function progressBar(value = 0, label = 'Progress') {
  const percent = clampPercent(value);
  return `<div class="cc-progress" aria-label="${escapeHtml(label)}: ${percent}%">
    <div class="cc-progress__meta"><span>${escapeHtml(label)}</span><strong>${percent}%</strong></div>
    <div class="cc-progress__track"><div class="cc-progress__bar" style="width:${percent}%"></div></div>
  </div>`;
}
